import React from 'react'
import PropTypes from 'prop-types'
import MetaData from './MetaData'

/**
 * Enhanced MetaData component for track pages with OG image support
 *
 * This component automatically generates the appropriate OG image URL
 * for track pages based on the track data provided
 */
const TrackMetaData = ({ track, location }) => {
    // Generate OG image URL
    const ogImageUrl = generateTrackOGImageUrl(track)

    // Create title and description
    const title = `${track.name} - ${track.city ? `${track.city}, ` : ''}${track.country} | Open Racer`
    const description = generateTrackDescription(track)

    return (
        <MetaData
            title={title}
            description={description}
            image={ogImageUrl}
            location={location}
        />
    )
}

/**
 * Generate OG image URL for a track
 * Returns path to pre-generated static image committed in repository
 */
function generateTrackOGImageUrl(track) {
    // Use circuitname for filename to match how OG images were generated
    const trackNameForFile = track.circuitname || track.name
    const safeFileName = trackNameForFile
        .replace(/[^a-z0-9]/gi, '-')
        .toLowerCase()
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')

    // Return path to pre-generated static image
    // All 575 OG images are committed in static/og-images/
    return `/og-images/${safeFileName}.png`
}

/**
 * Generate a descriptive meta description for a track
 */
function generateTrackDescription(track) {
    const parts = []

    if (track.type) {
        parts.push(`${track.type} track`)
    } else {
        parts.push('Race track')
    }

    parts.push(`in ${track.city || track.country}`)

    if (track.length) {
        parts.push(`| ${track.length}`)
    }

    if (track.turns) {
        parts.push(`| ${track.turns} turns`)
    }

    if (track.rating) {
        parts.push(`| ${track.rating}⭐ rating`)
    }

    parts.push('| Find directions, racing lines, and track information on Open Racer.')

    return parts.join(' ')
}

TrackMetaData.propTypes = {
    track: PropTypes.shape({
        name: PropTypes.string.isRequired,
        circuitname: PropTypes.string,
        country: PropTypes.string.isRequired,
        city: PropTypes.string,
        type: PropTypes.string,
        length: PropTypes.string,
        turns: PropTypes.number,
        rating: PropTypes.number,
        lapRecord: PropTypes.string,
    }).isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }).isRequired,
}

export default TrackMetaData
